import { Input } from "@bcc/components/ui/input";
import React, { useEffect, useRef, useState } from "react";

type NumericInputValue = number | null | undefined;

interface NumericInputProps extends Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "type" | "min" | "max"> {
  value: NumericInputValue;
  onChange: (value: number | null) => void;
}

const toRawValue = (value: NumericInputValue): string => (typeof value === "number" && Number.isFinite(value) ? String(value) : "");

const numericTextPattern = /^[+-]?(?:\d+\.?\d*|\.\d+)$/;

const parseRawValue = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  if (!numericTextPattern.test(trimmed)) return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Controlled number input that tracks raw string display state separately from
 * the parsed numeric value. This prevents the browser from blocking the user
 * from clearing the field when a `min` constraint would make the empty state
 * "invalid", and avoids the React controlled-`type="number"` display bug where
 * Chrome does not visually update the input when its value is programmatically
 * set to "" while focused.
 */
export const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(({ value, onChange, onBlur, onFocus, ...props }, ref) => {
  const [raw, setRaw] = useState(() => toRawValue(value));
  const rawRef = useRef(raw);
  rawRef.current = raw;
  const isFocusedRef = useRef(false);

  useEffect(() => {
    // Skip external sync while the user is actively editing so that a
    // parent form.reset() triggered by the onChange cycle cannot snap the
    // display back to the previous value mid-keystroke.
    if (isFocusedRef.current) return;
    const parsed = parseRawValue(rawRef.current);
    const synced =
      (typeof value === "number" && Number.isFinite(value) && parsed === value) ||
      (parsed === null && (value === null || value === undefined));
    if (!synced) {
      setRaw(toRawValue(value));
    }
    // Only re-run when `value` changes (i.e. form reset). Raw string is
    // accessed via ref so the effect does not subscribe to its changes.
  }, [value]);

  return (
    <Input
      {...props}
      ref={ref}
      type="text"
      inputMode="decimal"
      value={raw}
      onFocus={(e) => {
        isFocusedRef.current = true;
        onFocus?.(e);
      }}
      onBlur={(e) => {
        isFocusedRef.current = false;
        // Normalise display on blur: "3." → "3", "" stays "".
        const parsed = parseRawValue(raw);
        setRaw(parsed !== null ? String(parsed) : "");
        onBlur?.(e);
      }}
      onChange={(e) => {
        const str = e.target.value;
        setRaw(str);
        onChange(parseRawValue(str));
      }}
    />
  );
});

NumericInput.displayName = "NumericInput";
